import { euclideanDistance } from '@/modules/Editor/algebra'

import { differenceIslands, intersectionIslands, islandsOf, toSegment } from './booleans'
import type { Island, PolygonShape, Pt, Ring } from './geometry'
import {
  islandArea,
  islandContains,
  nearestOnSegment,
  ringsOf,
  segmentIntersection,
  shapeFromIslands,
} from './geometry'
import { MESSAGES } from './messages'
import type { Stroke } from './strokeGesture'

export type PolygonSlicePlan = { keep: PolygonShape; piece: PolygonShape }
type Result = PolygonSlicePlan | { error: string }

const HALF_STRIP = 0.005
const SNAP_DISTANCE = HALF_STRIP * 4
const FULL_SHARE = 1 - 1e-6

const islandRings = (island: Island): Ring[] => [island.outer, ...island.holes]
const totalArea = (islands: Island[]): number =>
  islands.reduce((sum, island) => sum + islandArea(island), 0)

const MITER_LIMIT = 4

// One closed outline of the cut, HALF_STRIP wide on each side and extended past both ends.
// A single polygon avoids unioning many hairline quads, which trips polybool's epsilon.
const stripOutline = (line: Pt[]): Ring | null => {
  const points = line.filter((p, i) => i === 0 || euclideanDistance(p, line[i - 1]) > 0)
  if (points.length < 2) {
    return null
  }
  const directions = points.slice(1).map((b, i) => {
    const length = euclideanDistance(points[i], b)
    return { x: (b.x - points[i].x) / length, y: (b.y - points[i].y) / length }
  })
  const normal = (d: Pt): Pt => ({ x: -d.y, y: d.x })
  const side = (sign: 1 | -1): Pt[] => {
    const out: Pt[] = []
    const first = points[0]
    const d0 = directions[0]
    const n0 = normal(d0)
    out.push({
      x: first.x - d0.x * HALF_STRIP + sign * n0.x * HALF_STRIP,
      y: first.y - d0.y * HALF_STRIP + sign * n0.y * HALF_STRIP,
    })
    for (let k = 1; k < points.length - 1; k++) {
      const a = normal(directions[k - 1])
      const b = normal(directions[k])
      const sum = { x: a.x + b.x, y: a.y + b.y }
      const sumLength = Math.hypot(sum.x, sum.y)
      const p = points[k]
      const miter =
        sumLength > 0 ? HALF_STRIP / ((sum.x * b.x + sum.y * b.y) / sumLength) : Infinity
      const da = directions[k - 1]
      const db = directions[k]
      // the inner side of a bend keeps its exact corner; bevelling it would cross the outline
      const inner = sign * (da.x * db.y - da.y * db.x) > 0
      if (!inner && miter > MITER_LIMIT * HALF_STRIP) {
        out.push(
          { x: p.x + sign * a.x * HALF_STRIP, y: p.y + sign * a.y * HALF_STRIP },
          { x: p.x + sign * b.x * HALF_STRIP, y: p.y + sign * b.y * HALF_STRIP },
        )
      } else {
        out.push({
          x: p.x + (sign * sum.x * miter) / sumLength,
          y: p.y + (sign * sum.y * miter) / sumLength,
        })
      }
    }
    const last = points[points.length - 1]
    const dn = directions[directions.length - 1]
    const nn = normal(dn)
    out.push({
      x: last.x + dn.x * HALF_STRIP + sign * nn.x * HALF_STRIP,
      y: last.y + dn.y * HALF_STRIP + sign * nn.y * HALF_STRIP,
    })
    return out
  }
  return [...side(1), ...side(-1).reverse()]
}

const nearestOnLine = (p: Pt, line: Pt[]): { point: Pt; distance: number } => {
  let best = { point: p, distance: Infinity }
  for (let i = 0; i < line.length - 1; i++) {
    const point = nearestOnSegment(p, line[i], line[i + 1])
    const d = euclideanDistance(p, point)
    if (d < best.distance) {
      best = { point, distance: d }
    }
  }
  return best
}

// Where the original edge through anchor -> p meets the cut; a bent cut can cross it several
// times, and the crossing that p came from is the one nearest to p.
const edgeCrossing = (anchor: Pt, p: Pt, line: Pt[]): Pt | null => {
  const far = { x: p.x + (p.x - anchor.x) * 1e3, y: p.y + (p.y - anchor.y) * 1e3 }
  let nearest: Pt | null = null
  for (let i = 0; i < line.length - 1; i++) {
    const crossing = segmentIntersection(anchor, far, line[i], line[i + 1])
    if (crossing && (!nearest || euclideanDistance(crossing, p) < euclideanDistance(nearest, p))) {
      nearest = crossing
    }
  }
  return nearest
}

const ORIGINAL_EPSILON = 1e-7

// Strip corners sit at the miter distance from a bend, which grows as the bend gets sharper.
const bendRadius = (line: Pt[], k: number): number => {
  if (k === 0 || k === line.length - 1) {
    return SNAP_DISTANCE
  }
  const a = { x: line[k - 1].x - line[k].x, y: line[k - 1].y - line[k].y }
  const b = { x: line[k + 1].x - line[k].x, y: line[k + 1].y - line[k].y }
  const angle = Math.acos(
    Math.max(
      -1,
      Math.min(1, (a.x * b.x + a.y * b.y) / (Math.hypot(a.x, a.y) * Math.hypot(b.x, b.y))),
    ),
  )
  return Math.max(SNAP_DISTANCE, (1.5 * HALF_STRIP) / Math.sin(angle / 2))
}

// Moves strip-edge vertices onto the cut so both pieces share one boundary.
// Vertices of the original outline are never moved, even when they sit next to the cut.
const snapRing = (ring: Ring, line: Pt[], original: Pt[]): Ring => {
  const isOriginal = (p: Pt): boolean =>
    original.some(
      (q) => Math.abs(q.x - p.x) < ORIGINAL_EPSILON && Math.abs(q.y - p.y) < ORIGINAL_EPSILON,
    )
  const onStrip = ring.map(
    (p) => nearestOnLine(p, line).distance <= SNAP_DISTANCE && !isOriginal(p),
  )
  const snapped = ring.map((p, i) => {
    if (!onStrip[i]) {
      return p
    }
    // an edge that does not run along the cut is part of the original outline, so its
    // direction locates the exact crossing even when both of its ends touch the cut
    const alongOutline = (q: Pt): boolean =>
      nearestOnLine({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, line).distance > SNAP_DISTANCE
    const previous = ring[(i - 1 + ring.length) % ring.length]
    const next = ring[(i + 1) % ring.length]
    const anchor = alongOutline(previous) ? previous : alongOutline(next) ? next : null
    const crossing = anchor && edgeCrossing(anchor, p, line)
    if (crossing) {
      return crossing
    }
    const bend = line.find((vertex, k) => euclideanDistance(vertex, p) <= bendRadius(line, k))
    return bend ?? nearestOnLine(p, line).point
  })
  return snapped.filter((p, i) => euclideanDistance(p, snapped[(i + 1) % snapped.length]) > 1e-9)
}

const snapIsland = (island: Island, line: Pt[], original: Pt[]): Island => ({
  outer: snapRing(island.outer, line, original),
  holes: island.holes.map((hole) => snapRing(hole, line, original)),
})

const isValidIsland = (island: Island): boolean =>
  island.outer.length >= 3 &&
  island.holes.every((hole) => hole.length >= 3) &&
  islandArea(island) > 0

const planLine = (islands: Island[], line: Pt[], target: Ring[]): Result => {
  if (islands.length > 1) {
    return { error: MESSAGES.multiPartLine }
  }
  const [island] = islands
  if ([line[0], line[line.length - 1]].some((p) => islandContains(island, p))) {
    return { error: MESSAGES.endsInside }
  }

  const strip = stripOutline(line)
  if (!strip) {
    return { error: MESSAGES.noDivide }
  }
  const pieces = differenceIslands(toSegment(target), toSegment([strip]))
  if (pieces.length < 2) {
    return { error: MESSAGES.noDivide }
  }
  if (pieces.length > 2) {
    return { error: MESSAGES.cutsMany(pieces.length) }
  }

  const original = target.flat()
  const [a, b] = pieces.map((piece) => snapIsland(piece, line, original))
  if (!isValidIsland(a) || !isValidIsland(b)) {
    return { error: MESSAGES.noDivide }
  }
  const [keep, piece] = islandArea(a) >= islandArea(b) ? [a, b] : [b, a]
  return { keep: shapeFromIslands([keep]), piece: shapeFromIslands([piece]) }
}

// On multi-part annotations the lasso moves exactly one whole island and never cuts one.
const planIslandLasso = (islands: Island[], lasso: Ring): Result => {
  const lassoSegment = toSegment([lasso])
  const shares = islands.map(
    (island) =>
      totalArea(intersectionIslands(toSegment(islandRings(island)), lassoSegment)) /
      islandArea(island),
  )
  if (shares.every((share) => share === 0)) {
    return { error: MESSAGES.lassoNoTouch }
  }
  if (shares.every((share) => share >= FULL_SHARE)) {
    return { error: MESSAGES.coversAll }
  }
  const enclosed = islands.filter((_, i) => shares[i] >= FULL_SHARE)
  const partial = shares.some((share) => share > 0 && share < FULL_SHARE)
  if (partial || enclosed.length !== 1) {
    return { error: MESSAGES.encloseOne }
  }
  return {
    keep: shapeFromIslands(islands.filter((_, i) => shares[i] === 0)),
    piece: shapeFromIslands(enclosed),
  }
}

const planLasso = (islands: Island[], lasso: Ring, target: Ring[]): Result => {
  if (islands.length > 1) {
    return planIslandLasso(islands, lasso)
  }
  const targetSegment = toSegment(target)
  const lassoSegment = toSegment([lasso])
  const inside = intersectionIslands(targetSegment, lassoSegment)
  const outside = differenceIslands(targetSegment, lassoSegment)
  if (!inside.length) {
    return { error: MESSAGES.lassoNoTouch }
  }
  if (!outside.length) {
    return { error: MESSAGES.coversAll }
  }
  if (inside.length !== 1 || outside.length !== 1) {
    return { error: MESSAGES.separatePieces }
  }
  return { keep: shapeFromIslands(outside), piece: shapeFromIslands(inside) }
}

export const planPolygonSlice = (
  target: { path: Pt[]; additionalPaths?: Pt[][] },
  stroke: Stroke,
): Result => {
  const rings = ringsOf(target).map((ring) => ring.map(({ x, y }) => ({ x, y })))
  // polybool can throw on numerically degenerate input; a preview must never crash
  try {
    const islands = islandsOf(toSegment(rings))
    if (!islands.length) {
      return { error: stroke.kind === 'line' ? MESSAGES.noDivide : MESSAGES.lassoNoTouch }
    }
    return stroke.kind === 'line'
      ? planLine(islands, stroke.points, rings)
      : planLasso(islands, stroke.points, rings)
  } catch {
    return { error: MESSAGES.geometryFailed }
  }
}
